
























import {
  defineComponent,
  computed,
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
} from '@vue/composition-api';
import bugsnagClient from '@getaccept/lib-shared/src/bugsnag';
import moment from 'moment';
import { Toasts } from '@getaccept/lib-shared/src/toast';
import { sanitizeEmail } from '@getaccept/lib-shared/src/helpers';
import { AuthenticationStepType } from '@getaccept/lib-shared/src/authentication/types/enums/authentication-step-type';
import { storeToRefs } from 'pinia';
import { DocumentStatus } from '@getaccept/lib-shared/src/enums/document-status';
import type { AuthenticationStep } from '@getaccept/lib-shared/src/authentication/types/authentication-step';
import { AuthenticationHelper } from '@getaccept/lib-shared/src/authentication/helpers/authentication.helper';
import { BackgroundType } from '@getaccept/lib-shared/src/entity/types/enums/background-type';
import Lobby from './identify/components/Lobby.vue';
import { useLoadDSR } from './published/composables/load-dsr.composable';
import { useSessionStore } from './session/store/session.store';
import { pusherClient } from './api/pusher/pusher-client';
import { useData } from './composables/use-data.composable';
import { useEntityStore } from './entity/store/entity.store';

export default defineComponent({
  components: {
    Lobby,
    Toasts,
  },
  setup() {
    const isDealroom21 = ref(true);
    const recalled = computed(() => !!status.value && status.value === DocumentStatus.Recalled);
    const unpublished = computed(
      () => !!status.value && status.value === DocumentStatus.Unpublished
    );

    const skipShowLobby = ref(false);
    const loading = ref(true);

    const { loadEntity } = useData();
    const { entity } = storeToRefs(useEntityStore());
    const { error: drError, dsr, theme, loadTheme } = useLoadDSR();
    const { submitAnswer, submitConsent, loadToken } = useSessionStore();
    const {
      authId,
      documentId,
      recipientId,
      token,
      status,
      sessionError,
      authentication,
      storedEmail,
    } = storeToRefs(useSessionStore());

    const showLobby = computed(() =>
      skipShowLobby.value
        ? false
        : !!nextAuthStep.value ||
          !!consentSteps.value ||
          hasError.value ||
          recalled.value ||
          unpublished.value ||
          gdprDeclined.value
    );

    const pageTitle = computed(() => (dsr.value ? dsr.value.name : 'GetAccept'));
    const hasFixedBackground = computed(
      () =>
        !theme.value ||
        theme.value.backgroundType === BackgroundType.Fixed ||
        !theme.value.backgroundImage
    );

    const hasParams = computed(() => {
      if (documentId.value) {
        return true;
      }
      return !!authId.value && !!documentId.value && !!recipientId.value;
    });

    const hasError = computed(() => sessionError.value || !hasParams.value || drError.value);

    const hasValidToken = computed(() => {
      const notExpired: boolean =
        (token.value.exp && !moment().isAfter(moment.unix(token.value.exp))) || !token.value.exp;
      const tokenFound = !!token.value.jwt;
      return tokenFound && notExpired;
    });

    const showDsrUnavailable = computed(
      () => hasError.value || unpublished.value || recalled.value
    );

    const backgroundStyle = computed(() =>
      theme.value?.backgroundImage
        ? `background-image: url(${theme.value?.backgroundImage});`
        : `background-image: url('/assets/img/background/signing-page-bg.svg');`
    );

    const setDocumentTitle = () => {
      window.document.title = pageTitle.value;
    };

    const preventIosInputZoom = () => {
      if (navigator.userAgent.indexOf('iPhone') > -1) {
        document
          .querySelector('[name=viewport]')
          .setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1');
      }
    };

    const unhandledrejectionHandler = event => {
      if (bugsnagClient) {
        bugsnagClient.notify(event);
      }
    };

    const nextAuthStep = computed(() => AuthenticationHelper.getNextAuthStep(authentication.value));

    const consentSteps = computed(() => {
      if (!authentication.value.steps.length) {
        return null;
      }
      return authentication.value.steps.filter(
        (step: AuthenticationStep) =>
          (step.type === AuthenticationStepType.TrackingConsent && step.data.error !== false) ||
          (step.type === AuthenticationStepType.GdprConsent && step.data.error !== false)
      );
    });

    const gdprDeclined = computed(() => {
      if (!authentication.value.steps.length) {
        return false;
      }
      return authentication.value.steps.some(
        (step: AuthenticationStep) =>
          step.type === AuthenticationStepType.GdprConsent && !!step.data.error
      );
    });

    const isPusherConnected = ref(false);
    const connectPusher = (token: string) => {
      if (isPusherConnected.value) {
        pusherClient.updateToken(token);
      } else {
        isPusherConnected.value = true;
        pusherClient.connect(token);
      }
    };

    watch(
      () => hasValidToken.value,
      () => {
        if (hasValidToken.value) {
          loadEntity();
          connectPusher(token.value.jwt);
        }
      }
    );

    onMounted(async () => {
      window.addEventListener('unhandledrejection', unhandledrejectionHandler);
      preventIosInputZoom();
      if (hasParams.value) {
        loadTheme(documentId.value);
      }
      if (!hasValidToken.value && hasParams.value) {
        const tracking = new URL(window.location.href).searchParams.get('tracking');
        await loadToken(
          tracking ? [{ type: AuthenticationStepType.TrackingConsent, value: tracking }] : []
        );
      }
      if (hasValidToken.value && hasParams.value && !showLobby.value) {
        loadEntity();
        connectPusher(token.value.jwt);
      }
      setDocumentTitle();
      const email = new URL(window.location.href).searchParams.get('email');
      const authSteps = authentication.value.steps;

      if (
        (email || storedEmail.value) &&
        authSteps.length === 1 &&
        authSteps[0].type === AuthenticationStepType.IdentifyRecipient
      ) {
        await submitAnswer([
          { type: nextAuthStep.value?.type, value: sanitizeEmail(email || storedEmail.value) },
        ]);

        if (!hasError.value) {
          skipShowLobby.value = true;
        }
      }
      loading.value = false;
    });

    onBeforeUnmount(() => {
      window.removeEventListener('unhandledrejection', unhandledrejectionHandler);
    });

    return {
      loading,
      isDealroom21,
      consentSteps,
      showDsrUnavailable,
      showLobby,
      skipShowLobby,
      backgroundStyle,
      hasValidToken,
      theme,
      entity,
      hasFixedBackground,
      pageTitle,
      hasParams,
      hasError,
      nextAuthStep,
      gdprDeclined,
      authentication,
      submitAnswer,
      submitConsent,
      loadTheme,
    };
  },
});
